import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productsAPI, categoriesAPI } from '../api';
import LocationPicker from '../components/LocationPicker';
import { useAuth } from '../hooks/useAuth';

const CreateProductPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [categoryTree, setCategoryTree] = useState([]);
  const [mainCatId, setMainCatId] = useState('');
  const [subCatId, setSubCatId] = useState('');
  const [subSubCatId, setSubSubCatId] = useState('');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    product_name: '', description: '', category_id: '', price: '',
    stock: '', condition: 'new', location: '', province_id: '', district_id: '', sector_id: '', is_countrywide: false,
  });

  useEffect(() => {
    categoriesAPI.getAll({ type: 'product' }).then((res) => {
      const tree = res.data.tree || [];
      setCategoryTree(tree);
      if (isEdit) {
        productsAPI.getById(id).then((pRes) => {
          const p = pRes.data.product;
          setForm({
            product_name: p.product_name || '', description: p.description || '', category_id: p.category_id || '',
            price: p.price || '', stock: p.stock || '', condition: p.condition || 'new',
            location: p.location || '', province_id: p.province_id || '', district_id: p.district_id || '', sector_id: p.sector_id || '',
            is_countrywide: p.is_countrywide || false,
          });
          if (p.images) {
            const imgs = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
            setExistingImages(Array.isArray(imgs) ? imgs : [imgs]);
          }
          const catId = p.category_id;
          for (const main of tree) {
            if (main.id === catId) { setMainCatId(main.id); return; }
            for (const sub of (main.children || [])) {
              if (sub.id === catId) { setMainCatId(main.id); setSubCatId(sub.id); return; }
              for (const subsub of (sub.children || [])) {
                if (subsub.id === catId) { setMainCatId(main.id); setSubCatId(sub.id); setSubSubCatId(subsub.id); return; }
              }
            }
          }
        }).catch(console.error);
      }
    }).catch(console.error);
  }, [id, isEdit]);

  const mainCat = categoryTree.find((c) => c.id === Number(mainCatId));
  const subCats = mainCat?.children || [];
  const subCat = subCats.find((c) => c.id === Number(subCatId));
  const subSubCats = subCat?.children || [];

  const addFiles = useCallback((files) => {
    const allowed = 5 - images.length - existingImages.length;
    const newFiles = Array.from(files).filter(f => f.type.startsWith('image/')).slice(0, Math.max(0, allowed));
    if (newFiles.length === 0) return;
    const withPreviews = newFiles.map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setImages(prev => [...prev, ...withPreviews]);
  }, [images.length, existingImages.length]);

  const removeImage = (idx) => {
    setImages(prev => { URL.revokeObjectURL(prev[idx].preview); return prev.filter((_, i) => i !== idx); });
  };
  const removeExisting = (idx) => setExistingImages(prev => prev.filter((_, i) => i !== idx));

  const handleDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }, [addFiles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) formData.append(k, v); });
      images.forEach(({ file }) => formData.append('images', file));
      if (isEdit && existingImages.length > 0) formData.append('existing_images', JSON.stringify(existingImages));

      if (isEdit) await productsAPI.update(id, formData);
      else await productsAPI.create(formData);

      setSuccess(true);
      setTimeout(() => navigate('/dashboard'), 1200);
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.limitReached) {
        setError(errData.error);
        setTimeout(() => {
          if (window.confirm(errData.error + '\n\nWould you like to upgrade your plan?')) {
            navigate('/subscription');
          }
        }, 500);
      } else {
        setError(errData?.error || 'Failed to save product');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 outline-none transition-all text-sm bg-white";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";
  const totalImages = images.length + existingImages.length;

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">{isEdit ? 'Product Updated!' : 'Product Listed!'}</h2>
          <p className="text-gray-500 text-sm">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user || (user.role !== 'provider' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Provider Account Required</h2>
          <p className="text-gray-500 mb-4">Only sellers and providers can post products.</p>
          <button onClick={() => navigate('/dashboard')} className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition">
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div>
              <h1 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Product' : 'Add New Product'}</h1>
              <p className="text-xs text-gray-500">Fill in the details below to list your product</p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
            Product
          </span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700 font-medium flex items-start gap-2">
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/></svg>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ─── Section: Photos (First for products!) ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
                <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">Product Photos</h2>
                <p className="text-xs text-gray-500">Great photos sell products ({totalImages}/5)</p>
              </div>
            </div>
            <div className="p-6">
              {/* Existing + new images grid */}
              {(existingImages.length > 0 || images.length > 0) && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4">
                  {existingImages.map((img, i) => (
                    <div key={`e-${i}`} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-100">
                      <img src={img.startsWith('http') ? img : `https://topinfo.rw${img}`} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeExisting(i)}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                      {i === 0 && <span className="absolute top-1.5 left-1.5 bg-white/90 text-[10px] font-bold px-1.5 py-0.5 rounded-md">Cover</span>}
                    </div>
                  ))}
                  {images.map((img, i) => (
                    <div key={`n-${i}`} className="relative group aspect-square rounded-xl overflow-hidden border border-gray-100">
                      <img src={img.preview} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(i)}
                        className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                      {existingImages.length === 0 && i === 0 && <span className="absolute top-1.5 left-1.5 bg-white/90 text-[10px] font-bold px-1.5 py-0.5 rounded-md">Cover</span>}
                    </div>
                  ))}
                </div>
              )}

              {totalImages < 5 && (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${dragOver ? 'border-primary-500 bg-primary-50/50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'}`}>
                  <svg className="w-10 h-10 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <p className="text-sm text-gray-600 font-medium">Drop images here or <span className="text-primary-600">browse</span></p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB each &middot; {5 - totalImages} remaining</p>
                  <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
                </div>
              )}
            </div>
          </div>

          {/* ─── Section: Basic Info ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">Product Details</h2>
                <p className="text-xs text-gray-500">Describe what you're selling</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={labelClass}>Product Name <span className="text-red-400">*</span></label>
                <input type="text" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  className={inputClass} placeholder="e.g., Samsung Galaxy A54, Handcrafted Basket" required maxLength={120} />
                <p className="text-xs text-gray-400 mt-1">{form.product_name.length}/120 characters</p>
              </div>
              <div>
                <label className={labelClass}>Description <span className="text-red-400">*</span></label>
                <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={`${inputClass} resize-none`} placeholder="Describe your product's features, condition, materials, dimensions..." required maxLength={2000} />
                <p className="text-xs text-gray-400 mt-1">{form.description.length}/2000 characters</p>
              </div>
            </div>
          </div>

          {/* ─── Section: Category ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                <svg className="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">Category</h2>
                <p className="text-xs text-gray-500">Help buyers find your product</p>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Main Category <span className="text-red-400">*</span></label>
                  <select value={mainCatId} onChange={(e) => { setMainCatId(e.target.value); setSubCatId(''); setSubSubCatId(''); setForm({ ...form, category_id: e.target.value }); }}
                    className={inputClass} required>
                    <option value="">Select category</option>
                    {categoryTree.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                {subCats.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Subcategory</label>
                    <select value={subCatId} onChange={(e) => { setSubCatId(e.target.value); setSubSubCatId(''); setForm({ ...form, category_id: e.target.value || mainCatId }); }}
                      className={inputClass}>
                      <option value="">Optional</option>
                      {subCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                {subSubCats.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Specific Type</label>
                    <select value={subSubCatId} onChange={(e) => { setSubSubCatId(e.target.value); setForm({ ...form, category_id: e.target.value || subCatId }); }}
                      className={inputClass}>
                      <option value="">Optional</option>
                      {subSubCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ─── Section: Pricing & Stock ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                <span className="text-green-600 font-bold text-sm">₣</span>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">Pricing & Inventory</h2>
                <p className="text-xs text-gray-500">Set your price and stock quantity</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Price (RWF) <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">RWF</span>
                    <input type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                      className={`${inputClass} pl-14`} placeholder="0" required />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Stock Quantity <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
                    </span>
                    <input type="number" min="1" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}
                      className={`${inputClass} pl-11`} placeholder="1" required />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Condition</label>
                  <div className="flex gap-2">
                    {[{ value: 'new', label: 'New', icon: '✨' }, { value: 'used', label: 'Used', icon: '♻️' }].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => setForm({ ...form, condition: opt.value })}
                        className={`flex-1 py-2.5 px-3 rounded-xl border-2 text-xs font-semibold transition-all ${form.condition === opt.value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'}`}>
                        <span className="block text-base mb-0.5">{opt.icon}</span>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ─── Section: Location ─── */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-50 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">Location</h2>
                <p className="text-xs text-gray-500">Where can buyers get this product?</p>
              </div>
            </div>
            <div className="p-6">
              <LocationPicker
                provinceId={form.province_id} districtId={form.district_id} sectorId={form.sector_id} isCountrywide={form.is_countrywide}
                onChange={({ province_id, district_id, sector_id, location_name, is_countrywide }) => setForm({ ...form, province_id, district_id, sector_id, location: location_name || form.location, is_countrywide: is_countrywide !== undefined ? is_countrywide : form.is_countrywide })}
                required />
            </div>
          </div>

          {/* ─── Submit ─── */}
          <div className="flex items-center gap-3 pt-2 pb-8">
            <button type="submit" disabled={loading}
              className="flex-1 bg-primary-600 text-white py-3.5 rounded-xl font-semibold hover:bg-primary-700 disabled:opacity-50 transition-all shadow-lg shadow-primary-500/20 hover:shadow-xl hover:shadow-primary-500/30 text-sm">
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                  Saving...
                </span>
              ) : isEdit ? 'Update Product' : 'List Product for Sale'}
            </button>
            <button type="button" onClick={() => navigate(-1)}
              className="px-8 py-3.5 border border-gray-200 rounded-xl font-semibold text-gray-600 hover:bg-gray-50 transition text-sm">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProductPage;
